import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { BubbleMenu, Editor, EditorContent, EditorEvents, useEditor } from '@tiptap/react';
import { KEY_VALUES } from 'common/util/dom';
import { BubbleMenuContent } from './bubble-menu';
import { extensions } from './extensions';
import styles from './index.module.styl';
const noop = () => {};
export const TipTapEditor = forwardRef(function TipTapEditor(_ref: {
  className?: string;
  autofocus: boolean;
  content: string;
  canEdit: boolean;
  onCreate?: (editorProps: EditorEvents[`create`]) => void;
  onTrack?: (eventName: string, props: Obj) => void;
  onUpdate?: (editorProps: EditorEvents[`update`]) => void;
  onSelectionUpdate?: (editorProps: EditorEvents[`selectionUpdate`]) => void;
  onBlur?: (editorProps: EditorEvents[`blur`]) => void;
  onFocus?: (editorProps: EditorEvents[`focus`]) => void;
  onDelete?: () => void;
  placeholder: string;
  alwaysShowPlaceHolder?: boolean;
}, ref: React.Ref<Editor | null>) {
  let {
    className = "",
    autofocus,
    content,
    canEdit,
    onTrack = noop,
    onUpdate = noop,
    onCreate = noop,
    onSelectionUpdate = noop,
    onBlur = noop,
    onFocus = noop,
    onDelete = noop,
    placeholder,
    alwaysShowPlaceHolder = false
  }: {
    className?: string;
    autofocus: boolean;
    content: string;
    canEdit: boolean;
    onCreate?: (editorProps: EditorEvents[`create`]) => void;
    onTrack?: (eventName: string, props: Obj) => void;
    onUpdate?: (editorProps: EditorEvents[`update`]) => void;
    onSelectionUpdate?: (editorProps: EditorEvents[`selectionUpdate`]) => void;
    onBlur?: (editorProps: EditorEvents[`blur`]) => void;
    onFocus?: (editorProps: EditorEvents[`focus`]) => void;
    onDelete?: () => void;
    placeholder: string;
    alwaysShowPlaceHolder?: boolean;
  } = _ref;
  const historyRef = React.useRef<boolean[]>([]);
  const editor = useEditor({
    editable: canEdit,
    content,
    extensions: extensions({
      placeholder,
      alwaysShowPlaceHolder
    }),
    // tell ProseMirror to ignore drop event
    editorProps: {
      handleDOMEvents: {
        drop: (_view, ev) => {
          ev.preventDefault();
          return false;
        }
      }
    },
    onUpdate(editorProps) {
      // We need to keep the latest two empty state histories here.
      // When pressing backspace - which should delete an empty card for us -
      // tiptap first updates the editor then emits the update event before the keydown is registered.
      // This means that at keydown, the editor will be empty from the update.
      // If a user - for example - selected all text and then hit backspace, by the time the keydown registers
      // the editor will be empty, the previous state will be empty and thus we would delete the card.
      // But in that case, we only want to delete the text - not the card. To remedy this, we keep the latest two states and
      // check that the previous state (emptyStateHistory[0]) was also empty before deleting.
      historyRef.current = [historyRef.current[1], editorProps.editor.isEmpty];
      onUpdate(editorProps);
    },
    onCreate(editorProps) {
      historyRef.current = [editorProps.editor.isEmpty, editorProps.editor.isEmpty];
      onCreate(editorProps);
    },
    onSelectionUpdate,
    onBlur(editorProps) {
      editorProps.editor.commands.blur();
      onBlur(editorProps);
    },
    onFocus,
    autofocus
  }, [canEdit]);
  // incase parent want to access editor instance
  useImperativeHandle(ref, () => {
    return editor;
  }, [editor]);

  // this is a workaround to force focus from outside of the component
  useEffect(() => {
    if (autofocus && editor) {
      // need to focus async because of react lifecycle stuffs
      requestAnimationFrame(() => {
        editor.commands.focus();
      });
    }
  }, [editor, autofocus]);

  // this is to make editor responsive. when content changes it sets the text to the correct value
  useEffect(() => {
    if (editor) {
      // if the content passed in matches current state, then it's active edit from the user themselves
      if (editor.getHTML() === content) {
        return;
      }
      // if the user is actively editing, we don't want to set the content as well
      if (editor.isFocused) {
        return;
      }
      // otherwise it's a edit from other users (multiplayer mode)
      editor.commands.setContent(content, false, {
        preserveWhitespace: "full"
      });
    }
  }, [editor, content]);
  return <div className={clsx(className, styles.tiptapEditor)}>
      {editor && <BubbleMenu editor={editor} shouldShow={editorProps => {
      return editorProps.editor.options.editable && !editorProps.state.selection.empty;
    }} tippyOptions={{
      appendTo: document.body,
      popperOptions: {
        strategy: "fixed",
        modifiers: [{
          name: "computeStyles",
          options: {
            adaptive: false,
            gpuAcceleration: false
          }
        }]
      }
    }}>
          <BubbleMenuContent editor={editor} onTrack={onTrack} />
        </BubbleMenu>}
      <EditorContent editor={editor} spellCheck={canEdit && (editor === null || editor === void 0 ? void 0 : editor.isFocused)}
    // important here. mouse down is sometimes triggered in capture phase
    // it will prevent tiptap to properly handle mouse down event causing
    // the cursor selection to be not handled properly
    onMouseDownCapture={ev => {
      ev.stopPropagation();
    }} onKeyDown={ev => {
      ev.stopPropagation();
      if (ev.key === KEY_VALUES.ESCAPE) {
        // if there's active selection, we collapse to the end of selection and hide bubble menu
        // otherwise the user has a cursor and typed `Escape`, they want to blur the editor
        const selection = window.getSelection();
        selection !== null && selection !== void 0 && selection.isCollapsed ? editor === null || editor === void 0 ? void 0 : editor.commands.blur() : selection === null || selection === void 0 ? void 0 : selection.collapseToEnd();
      }
      // this makes sure that when selectAll -> delete, it deletes text content and then deletes the card
      if (ev.key === KEY_VALUES.BACKSPACE && editor !== null && editor !== void 0 && editor.isEmpty && historyRef.current[0]) {
        ev.preventDefault();
        onDelete();
      }
    }} style={{
      width: "100%",
      overflow: "auto",
      minHeight: "40px"
    }} />
    </div>;
});
TipTapEditor.displayName = "TipTapEditor";